import {
  EmrAudits,
  EmrBillings,
  EmrCharts,
  EmrCheckmarkShield,
  EmrCloudCheck,
  EmrContrastList,
  EmrDuplicatePatients,
  EmrHeartBoltSquare,
  EmrHeartSquared,
  EmrIntegrationGlobe,
  EmrKey,
  EmrLabs,
  EmrLabsList,
  EmrLogs,
  EmrMedicalRecords,
  EmrMedicationList,
  EmrOverview,
  EmrPermissions,
  EmrPerson,
  EmrQuestionMarkCircle,
  EmrRadiology,
  EmrRadiologyList,
  EmrRoomCard,
  EmrRoomList,
  EmrStpVipList,
  EmrTreatmentList,
  SiGear,
  SiPhoneMaterial,
} from "@medstonetech/slate-icons";

import { Box, Grid, HStack, Icon, VStack } from "@chakra-ui/react";
import background from "assets/background-welcome.jpg";
import { usePermissions } from "contexts";
import { useUserRoles } from "contexts/UserRoles";

import { ModuleLink, ModuleLinkProps } from "./ModuleLink";
import {
  DuplicatePatientsNavigationCard,
  IncompleteAuditsNavigationCard,
  OpenChartsNavigationCard,
} from "./NavigationCard";

import { UserType } from "modules/identity";
import { Outlet } from "react-router-dom";
import { UserCard } from "shared/user-card";
import { CurrentDateTime } from "./CurrentDateTime";
import { PasswordsManager } from "./passwords-manager";
import { SystemRequirementsCard } from "./system-requirements";

function Onboarding() {
  const { roles } = useUserRoles();
  const { scope } = usePermissions();

  const MODULES: ModuleLinkProps[] = [
    {
      to: "/reception",
      moduleIcon: <Icon as={EmrKey} />,
      label: "Reception",
      bg: "linear-gradient(180deg, #7CDBFA 0%, #5AC9ED 100%)",
      hasAccess: scope("reception").isAccessible,
    },
    {
      to: "/in-process",
      moduleIcon: <Icon as={EmrPerson} />,
      label: "In Process",
      bg: "linear-gradient(180deg, #3573E7 0%, #1A5BD6 100%);",
      hasAccess: scope("inprocess").isAccessible,
    },
    {
      to: "/labs",
      moduleIcon: <Icon as={EmrLabs} />,
      label: "Labs",
      bg: "linear-gradient(180deg, #FD9840 0%, #F07A13 100%)",
      hasAccess: scope("labs").isAccessible,
    },
    {
      to: "/radiology",
      moduleIcon: <Icon as={EmrRadiology} />,
      label: "Radiology",
      bg: "linear-gradient(180deg, #9557F3 0%, #692FC2 100%)",
      hasAccess: scope("radiology").isAccessible,
    },
    {
      to: "/audits",
      moduleIcon: <Icon as={EmrAudits} />,
      label: "Audits",
      bg: "#FF9500",
      hasAccess: scope("audits").isAccessible,
    },
    {
      to: "/billings",
      moduleIcon: <Icon as={EmrBillings} />,
      label: "Billings",
      bg: "#34C759",
      hasAccess: scope("billings").isAccessible,
    },
    {
      to: "/open-charts",
      moduleIcon: <Icon as={EmrCharts} />,
      label: "Open Charts",
      bg: "red",
      hasAccess: scope("opencharts").isAccessible,
    },
    {
      to: "/permissions",
      moduleIcon: <Icon as={EmrPermissions} />,
      label: "Permissions",
      bg: "green",
      hasAccess: scope("permission").isAccessible,
    },
    {
      to: "/stp-vip",
      moduleIcon: <Icon as={EmrStpVipList} />,
      label: "STP/VIP List",
      bg: "red",
      hasAccess: scope("stpvip").isAccessible,
    },
    {
      to: "/medical-records",
      moduleIcon: <Icon as={EmrMedicalRecords} />,
      label: "Medical Records",
      bg: "mint",
      hasAccess: scope("medicalrecords").isAccessible,
    },
    {
      to: "/home/labs-list",
      moduleIcon: <Icon as={EmrLabsList} />,
      label: "Labs List",
      bg: "yellow",
      hasAccess: scope("labslist").isAccessible,
    },
    {
      to: "/home/radiology-list",
      moduleIcon: <Icon as={EmrRadiologyList} />,
      label: "Radiology List",
      bg: "red",
      hasAccess: scope("radiologylist").isAccessible,
    },
    {
      to: "/home/medication-list",
      moduleIcon: <Icon as={EmrMedicationList} />,
      label: "Medication List",
      bg: "mint",
      hasAccess: scope("medicationlist").isAccessible,
    },
    {
      to: "/home/contrast-list",
      moduleIcon: <Icon as={EmrContrastList} />,
      label: "Contrast List",
      bg: "yellow",
      hasAccess: scope("contrastlist").isAccessible,
    },
    {
      to: "/home/treatments-list",
      moduleIcon: <Icon as={EmrTreatmentList} />,
      label: "Treatments List",
      bg: "#AF52DE",
      hasAccess: scope("treatmentslist").isAccessible,
    },
    {
      to: "/home/ekg-list",
      moduleIcon: <Icon as={EmrHeartBoltSquare} />,
      label: "Cardio List",
      bg: "cyan",
      hasAccess: scope("ekglist").isAccessible,
    },
    {
      to: "/home/logs",
      moduleIcon: <Icon as={EmrLogs} />,
      label: "Logs",
      bg: "mint",
      hasAccess: scope("logs").isAccessible,
    },
    {
      to: "/insurance-payers",
      moduleIcon: <Icon as={EmrCheckmarkShield} />,
      label: "Insurance Payers",
      bg: "linear-gradient(180deg, #9557F3 0%, #692FC2 100%)",
      hasAccess: scope("insurancepayers").isAccessible,
    },
    {
      to: "/home/room-management",
      moduleIcon: <Icon as={EmrRoomCard} />,
      label: "Rooms Management",
      bg: "cyan",
      hasAccess: scope("roomsmanagement").isAccessible,
    },
    {
      to: "/home/room-list",
      moduleIcon: <Icon as={EmrRoomList} />,
      label: "Rooms List",
      bg: "cyan",
      hasAccess: scope("roomslist").isAccessible,
    },
    {
      to: "/whitelist",
      moduleIcon: <Icon as={EmrCloudCheck} />,
      label: "Whitelist",
      bg: "green",
      hasAccess: scope("ipwhitelist").isAccessible,
    },
    {
      to: "/integrations",
      moduleIcon: <Icon as={EmrIntegrationGlobe} />,
      label: "Integrations",
      bg: "cyan",
      hasAccess: scope("integrations").isAccessible,
    },
    {
      to: "/duplicate-patients",
      moduleIcon: <Icon as={EmrDuplicatePatients} />,
      label: "Duplicate Patients",
      bg: "yellow",
      hasAccess: scope("duplicatepatients").isAccessible,
    },
    {
      to: "/home/faq",
      moduleIcon: <Icon as={EmrQuestionMarkCircle} />,
      label: "FAQ",
      bg: "blue",
      hasAccess: scope("faq").isAccessible,
    },
    // {
    //   to: "/teams",
    //   moduleIcon: <Icon as={Teams} />,
    //   label: "Teams",
    //   bg: "linear-gradient(180deg, #7CDBFA 0%, #5AC9ED 100%)",
    //   isEnabled: false,
    // },
    {
      to: "/hospitality",
      moduleIcon: <Icon as={EmrHeartSquared} />,
      label: "Hospitality",
      bg: "red-sizzling",
      hasAccess: scope("hospitality").isAccessible,
    },
    {
      to: "/callbacks",
      moduleIcon: <Icon as={SiPhoneMaterial} />,
      label: "Callbacks",
      bg: "teal",
      hasAccess: scope("callbacks").isAccessible,
    },
    {
      to: "/overview",
      moduleIcon: <Icon as={EmrOverview} />,
      label: "Overview",
      bg: "teal",
      hasAccess: scope("overview").isAccessible,
    },
    {
      to: "/chart-engine",
      moduleIcon: <Icon as={SiGear} />,
      label: "Chart Engine",
      bg: "gray",
      hasAccess: scope("chartengine").isAccessible,
    },
    // {
    //   to: "/billing",
    //   moduleIcon: <Icon as={Bill} />,
    //   label: "Billing",
    //   bg: "linear-gradient(180deg, #0BB839 0%, #058527 100%)",
    //   isEnabled: false,
    // },
    // {
    //   to: "/task-manager",
    //   moduleIcon: <Icon as={CheckList} />,
    //   label: "Task Manager",
    //   bg: "linear-gradient(180deg, #FD9840 0%, #F07A13 100%)",
    //   isEnabled: false,
    // },
    // {
    //   to: "/callbacks",
    //   moduleIcon: <Icon as={Phone} />,
    //   label: "Callbacks",
    //   bg: "linear-gradient(180deg, #0BB839 0%, #058527 100%)",
    //   isEnabled: false,
    // },
    // {
    //   to: "/maintenance",
    //   moduleIcon: <Icon as={Tools} />,
    //   label: "Maintenance",
    //   bg: "linear-gradient(180deg, #3573E7 0%, #1A5BD6 100%)",
    //   isEnabled: false,
    // },
    // {
    //   to: "/kitchen",
    //   moduleIcon: <Icon as={EmrKitchen} />,
    //   label: "Kitchen",
    //   bg: "linear-gradient(180deg, #DB4B3D 0%, #D03728 100%)",
    //   isEnabled: false,
    // },
    // {
    //   to: "/supplies",
    //   moduleIcon: <Icon as={EmrCrossCase} />,
    //   label: "Supplies",
    //   bg: "linear-gradient(180deg, #FDCE30 0%, #F8C109 100%)",
    //   isEnabled: false,
    // },
  ];

  const rolesAllowedToViewDuplicatePatientsCounter: UserType[] = [
    "Administrator",
  ];

  const rolesAllowedToViewOpenChartCounter: UserType[] = [
    "Administrator",
    "SupervisingProvider",
    "Provider",
    "Nurse",
  ];

  return (
    <Box
      display="flex"
      padding="3.5rem"
      height="100%"
      bgImage={background}
      bgPos="center"
      bgRepeat="no-repeat"
      bgSize="cover"
    >
      <Box width="30rem" marginRight="5rem">
        <CurrentDateTime marginBottom="2rem" />
        <UserCard marginBottom="1rem" flexDir="column" />

        <VStack spacing="1rem">
          <HStack w="100%" alignItems="start" gap="1rem">
            {roles.some((role) =>
              rolesAllowedToViewOpenChartCounter.includes(role)
            ) && <OpenChartsNavigationCard flexDir="column" />}

            {roles.some((role) =>
              rolesAllowedToViewOpenChartCounter
                .concat("Auditor")
                .includes(role)
            ) && <IncompleteAuditsNavigationCard flexDir="column" />}
          </HStack>
          <HStack w="100%" alignItems="start" gap="1rem">
            {roles.some((role) =>
              rolesAllowedToViewDuplicatePatientsCounter.includes(role)
            ) && (
              <DuplicatePatientsNavigationCard
                marginBottom="1rem"
                flexDir="column"
              />
            )}
          </HStack>
        </VStack>
        <VStack justify="end" alignItems="start" w="100%">
          <SystemRequirementsCard />
        </VStack>
      </Box>
      {/**
       * @todo - Add modules to grid once each module
       * development is done
       */}
      <Grid
        flex={1}
        columnGap="2rem"
        gridTemplateColumns="repeat(5, 1fr)"
        rowGap="2rem"
        height="fit-content"
        maxHeight="100%"
        overflow="auto"
        paddingTop="25px"
      >
        {MODULES.map(
          (module) =>
            module.hasAccess && <ModuleLink key={module.to} {...module} />
        )}
        {scope("resetpassword").isAccessible && <PasswordsManager />}
      </Grid>
      <Outlet />
    </Box>
  );
}
export { Onboarding };
